import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Battery, Shield, Truck, Award } from 'lucide-react';
import axios from 'axios';
import ProductCard from '../components/ProductCard';
import { useLanguage } from '../context/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Home = () => {
  const [bestsellers, setBestsellers] = useState([]);
  const [loading, setLoading] = useState(true);
  const { t } = useLanguage();

  useEffect(() => {
    const fetchBestsellers = async () => {
      try {
        const response = await axios.get(`${API}/products?bestseller=true`);
        setBestsellers(response.data.slice(0, 4));
      } catch (error) {
        console.error('Failed to fetch bestsellers:', error);
      } finally {
        setLoading(false);
      }
    };

    axios.post(`${API}/seed`).then(() => {
      fetchBestsellers();
    }).catch(() => {
      fetchBestsellers();
    });
  }, []);

  const categories = [
    {
      id: 'dewalt-batteries',
      name: t.nav.powerTools,
      description: t.home.powerToolsDesc,
      image: 'https://images.unsplash.com/photo-1632095710940-ad578e8cbe6b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwxfHxEZVdhbHQlMjBiYXR0ZXJ5JTIwcG93ZXIlMjB0b29sJTIweWVsbG93fGVufDB8fHx8MTc3NDUzNjIwNHww&ixlib=rb-4.1.0&q=85'
    },
    {
      id: 'milwaukee-batteries',
      name: t.nav.handTools,
      description: t.home.handToolsDesc,
      image: 'https://images.unsplash.com/photo-1504148455328-c376907d081c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwzfHxyZWQlMjBwb3dlciUyMHRvb2wlMjBjb25zdHJ1Y3Rpb24lMjBwcm9mZXNzaW9uYWwlMjBoZWF2eSUyMGR1dHl8ZW58MHx8fHwxNzc0NTYwNjE4fDA&ixlib=rb-4.1.0&q=85'
    },
    {
      id: 'makita-batteries',
      name: t.nav.accessories,
      description: t.home.accessoriesDesc,
      image: 'https://images.unsplash.com/photo-1751486403850-fae53b6ab0e5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwxfHxNYWtpdGElMjBiYXR0ZXJ5JTIwdGVhbCUyMHBvd2VyJTIwdG9vbHxlbnwwfHx8fDE3NzQ1MzYyMDV8MA&ixlib=rb-4.1.0&q=85'
    },
    {
      id: 'tools-accessories',
      name: t.nav.toolsAccessories,
      description: t.home.toolsAccessoriesDesc,
      image: 'https://images.pexels.com/photos/5432282/pexels-photo-5432282.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940'
    }
  ];

  const features = [
    { icon: Shield, title: t.home.qualityGuaranteed, description: t.home.qualityDesc },
    { icon: Truck, title: t.home.fastShipping, description: t.home.shippingDesc },
    { icon: Award, title: t.home.expertSupport, description: t.home.supportDesc }
  ];

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative w-full h-[80vh] min-h-[600px] flex items-center border-b border-zinc-800" data-testid="hero-section">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ 
            backgroundImage: 'url(https://static.prod-images.emergentagent.com/jobs/414525f8-1b8f-49b0-92d6-32a6c385546c/images/abca9e2f004c394e7aac0a805948519ee8586550742aea86fb6b7e96bd911b21.png)'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/95 via-black/70 to-transparent" />

        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="font-mono text-xs tracking-[0.2em] uppercase text-yellow-500 mb-4 block" data-testid="hero-label">
              {t.home.heroLabel}
            </span>
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-[5rem] tracking-tighter uppercase leading-[0.9] text-white mb-6" data-testid="hero-title">
              {t.home.heroTitle1}<br />
              <span className="text-yellow-400">{t.home.heroTitle2}</span>
            </h1>
            <p className="text-lg md:text-xl text-zinc-300 mb-8 max-w-xl">
              {t.home.heroDescription}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link 
                to="/shop" 
                className="bg-yellow-400 text-black uppercase font-bold tracking-widest px-8 py-4 border-2 border-yellow-400 hover:bg-black hover:text-yellow-400 transition-colors inline-flex items-center justify-center gap-2"
                data-testid="hero-shop-button"
              >
                {t.home.shopNow} <ArrowRight size={20} />
              </Link>
              <Link 
                to="/about" 
                className="bg-transparent text-white uppercase font-bold tracking-widest px-8 py-4 border-2 border-zinc-700 hover:border-white transition-colors inline-flex items-center justify-center"
                data-testid="hero-about-button"
              >
                {t.home.learnMore}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features Bar */}
      <section className="bg-[#111111] border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-0 md:divide-x divide-zinc-800">
            {features.map((feature, index) => (
              <div key={index} className="flex items-center gap-4 md:px-8 first:md:pl-0 last:md:pr-0">
                <feature.icon size={32} className="text-yellow-400" strokeWidth={2} />
                <div>
                  <h3 className="font-bold text-white">{feature.title}</h3>
                  <p className="text-sm text-zinc-400">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-16 md:py-24" data-testid="categories-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12">
            <div>
              <span className="font-mono text-xs tracking-[0.2em] uppercase text-yellow-500 mb-2 block">
                {t.home.browseLabel}
              </span>
              <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tighter uppercase leading-none text-white">
                {t.home.categories}
              </h2>
            </div>
            <Link 
              to="/shop" 
              className="mt-4 md:mt-0 font-mono text-sm tracking-widest uppercase text-zinc-400 hover:text-white transition-colors inline-flex items-center gap-2"
            >
              {t.home.viewAll} <ArrowRight size={16} />
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-0">
            {categories.map((category) => (
              <Link 
                key={category.id}
                to={`/shop/${category.id}`}
                className="category-card relative border border-zinc-800 group overflow-hidden"
                data-testid={`category-${category.id}-link`}
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img 
                    src={category.image} 
                    alt={category.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <Battery size={24} className="text-yellow-400 mb-3" strokeWidth={2} />
                  <h3 className="font-heading text-xl lg:text-2xl uppercase text-white group-hover:text-yellow-400 transition-colors">
                    {category.name}
                  </h3>
                  <p className="text-xs lg:text-sm text-zinc-400 mt-1">{category.description}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Bestsellers Section */}
      <section className="py-16 md:py-24 bg-[#111111] border-t border-b border-zinc-800" data-testid="bestsellers-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12">
            <div>
              <span className="font-mono text-xs tracking-[0.2em] uppercase text-yellow-500 mb-2 block">
                {t.home.topPicks}
              </span>
              <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tighter uppercase leading-none text-white">
                {t.home.bestSellers}
              </h2>
            </div>
            <Link 
              to="/shop" 
              className="mt-4 md:mt-0 font-mono text-sm tracking-widest uppercase text-zinc-400 hover:text-white transition-colors inline-flex items-center gap-2"
            >
              {t.home.shopAll} <ArrowRight size={16} />
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-0">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="border border-zinc-800 p-6 animate-pulse">
                  <div className="aspect-square bg-zinc-800 mb-4" />
                  <div className="h-4 bg-zinc-800 mb-2 w-1/3" />
                  <div className="h-6 bg-zinc-800 mb-4" />
                  <div className="h-6 bg-zinc-800 w-1/2" />
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-0">
              {bestsellers.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24" data-testid="cta-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="border border-zinc-800 bg-[#111111] p-8 md:p-16 text-center">
            <span className="font-mono text-xs tracking-[0.2em] uppercase text-yellow-500 mb-4 block">
              {t.home.readyToBuild}
            </span>
            <h2 className="font-heading text-4xl sm:text-5xl tracking-tighter uppercase leading-none text-white mb-6">
              {t.home.getRightTools}
            </h2>
            <p className="text-zinc-400 mb-8 max-w-2xl mx-auto">
              {t.home.ctaDescription}
            </p>
            <Link 
              to="/shop" 
              className="bg-yellow-400 text-black uppercase font-bold tracking-widest px-8 py-4 border-2 border-yellow-400 hover:bg-black hover:text-yellow-400 transition-colors inline-flex items-center gap-2"
              data-testid="cta-shop-button"
            >
              {t.home.startShopping} <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
